import { getBuildingDisplayHeight } from '@popety-io/utils'
import React, { useEffect, useState, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { useCustomTheme } from '../../providers/CustomThemeProvider'
import { useResolvedThemeColors } from '../../hooks/useResolvedThemeColors'
import {
  Container,
  Stack,
  Text,
  Loading,
  Icon,
  Button,
  PropertyHeader,
  PageContainer,
  MainContainer,
  ContentWrapper,
  MainContent,
  MapWrapper,
  MapCloseButton,
  MobileMapToggle,
  AnalysisSidebar,
  NavSectionType,
  AnalysisMapSectionDataProvider,
} from '@popety-io/ui'
import { PublicLayout } from '../../components/PublicLayout'
import { publicApiService } from '../../services/publicApiService'
import { BuildingAnalysisContent } from './components/BuildingAnalysisContent'
import { BuildingAnalysisMap } from './components/BuildingAnalysisMap'
import { FullHeightContainer } from '../../components/shared'
import { MapSectionDataProvider } from './contexts'
import { PublicAnalysisProvider } from '../../providers/PublicAnalysisProvider'

const PublicBuildingAnalysisPage: React.FC = () => {
  const { publicId: hash } = useParams<{ publicId: string }>()
  const { t, i18n } = useTranslation('building-analysis')
  const { t: tCommon } = useTranslation('common')

  // Theme customization
  const { updateThemeConfig } = useCustomTheme()

  const [buildingData, setBuildingData] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeSection, setActiveSection] = useState('summary')
  const [isMobileMapOpen, setIsMobileMapOpen] = useState(false)
  const [mapWidth, setMapWidth] = useState(580)
  const [isResizing, setIsResizing] = useState(false)

  // Resolve theme colors from company/office data
  const resolvedThemeConfig = useResolvedThemeColors(buildingData)

  // Update theme when data is loaded
  useEffect(() => {
    if (buildingData && resolvedThemeConfig) {
      updateThemeConfig(resolvedThemeConfig)
    }
  }, [buildingData, resolvedThemeConfig, updateThemeConfig])

  // Navigation sections for building analysis
  const navigationSections: NavSectionType[] = useMemo(
    () => [
      {
        title: tCommon('sidebar.sections.overview'),
        color: 'steel',
        items: [
          {
            id: 'summary',
            label: tCommon('sidebar.items.summary'),
            icon: 'bar-chart',
          },
          {
            id: 'history',
            label: tCommon('sidebar.items.history'),
            icon: 'clock',
          },
          {
            id: 'building-characteristics',
            label: tCommon('sidebar.items.buildingCharacteristics'),
            icon: 'cube',
          },
          {
            id: 'unit-details',
            label: tCommon('sidebar.items.unitDetails'),
            icon: 'building-2',
          },
          {
            id: 'energy',
            label: tCommon('sidebar.items.energy'),
            icon: 'battery-charging',
          },
          {
            id: 'notes',
            label: tCommon('sidebar.items.notes'),
            icon: 'edit',
          },
          {
            id: 'files',
            label: tCommon('sidebar.items.files'),
            icon: 'file',
          },
        ],
      },
      {
        title: tCommon('sidebar.sections.development'),
        color: 'bronze',
        items: [
          {
            id: 'building-permits',
            label: tCommon('sidebar.items.buildingPermits'),
            icon: 'building-permit',
          },
        ],
      },
      {
        title: tCommon('sidebar.sections.market'),
        color: 'forest',
        items: [
          {
            id: 'purchase-market',
            label: tCommon('sidebar.items.purchaseMarket'),
            icon: 'cash',
          },
          {
            id: 'rental-market',
            label: tCommon('sidebar.items.rentalMarket'),
            icon: 'home',
          },
          {
            id: 'purchase-listings',
            label: tCommon('sidebar.items.purchaseListings'),
            icon: 'listing-purchase',
          },
          {
            id: 'rental-listings',
            label: tCommon('sidebar.items.rentalListings'),
            icon: 'listing-rental',
          },
          {
            id: 'transactions',
            label: tCommon('sidebar.items.transactions'),
            icon: 'real-estate-transaction',
          },
        ],
      },
      {
        title: tCommon('sidebar.sections.environment'),
        color: 'deepBlue',
        items: [
          {
            id: 'transport-access',
            label: tCommon('sidebar.items.transportAccess'),
            icon: 'car',
          },
          {
            id: 'amenities',
            label: tCommon('sidebar.items.amenities'),
            icon: 'store',
          },
          {
            id: 'noise',
            label: tCommon('sidebar.items.noiseAnalysis'),
            icon: 'activity',
          },
          {
            id: 'sunlight',
            label: tCommon('sidebar.items.sunlightStudy'),
            icon: 'sun',
          },
        ],
      },
      {
        title: tCommon('sidebar.sections.socialEconomic'),
        color: 'wine',
        items: [
          {
            id: 'demographics',
            label: tCommon('sidebar.items.demographics'),
            icon: 'users',
          },
          {
            id: 'employment',
            label: tCommon('sidebar.items.employment'),
            icon: 'briefcase',
          },
          {
            id: 'taxes',
            label: tCommon('sidebar.items.taxes'),
            icon: 'receipt',
          },
          {
            id: 'electricity',
            label: tCommon('sidebar.items.electricity'),
            icon: 'zap',
          },
        ],
      },
    ],
    [tCommon]
  )

  // Filter navigation sections based on available sections
  const filteredNavigationSections = useMemo(() => {
    let sections = navigationSections.map((section) => ({
      ...section,
      items: section.items.filter(
        (item) => item.id !== 'notes' && item.id !== 'files'
      ),
    }))

    if (
      Array.isArray(buildingData?.available_sections) &&
      buildingData.available_sections.length > 0
    ) {
      sections = navigationSections
      // Map navigation IDs to data section IDs (must match BuildingAnalysisSectionId type)
      const sectionIdMap: Record<string, string> = {
        summary: 'summary',
        'building-characteristics': 'characteristics',
        'unit-details': 'units',
        energy: 'energy',
        history: 'history',
        'upper-elevation': 'potential',
        'building-permits': 'building_permits',
        feasibility: 'feasibility',
        'purchase-market': 'purchase_market',
        'rental-market': 'rental_market',
        'purchase-listings': 'purchase_listings',
        'rental-listings': 'rental_listings',
        transactions: 'transactions',
        'transport-access': 'transport',
        amenities: 'amenities',
        noise: 'noise',
        sunlight: 'sunlight',
        demographics: 'demographics',
        employment: 'employment',
        taxes: 'taxes',
        electricity: 'electricity',
        notes: 'notes',
        files: 'files',
      }

      // Filter out sections that are not in the available_sections array
      sections = sections
        .map((section) => ({
          ...section,
          items: section.items.filter((item) => {
            const mappedId = sectionIdMap[item.id] || item.id
            const isAvailable =
              buildingData.available_sections!.includes(mappedId)
            return isAvailable
          }),
        }))
        .filter((section) => section.items.length > 0)
    }

    return sections
  }, [buildingData?.available_sections, navigationSections])

  useEffect(() => {
    const visible = filteredNavigationSections.some((section) =>
      section.items.some((item) => item.id === activeSection)
    )
    const first = filteredNavigationSections[0]?.items[0]?.id
    if (!visible && first) setActiveSection(first)
  }, [filteredNavigationSections, activeSection])

  useEffect(() => {
    const fetchBuildingData = async () => {
      if (!hash) return

      try {
        setLoading(true)
        setError(null)
        const data = await publicApiService.getBuildingAnalysis(hash)
        setBuildingData(data)

        // Set language to the share creator's preference
        const userLang = data.user?.language
        if (userLang && ['en', 'fr', 'de', 'it'].includes(userLang)) {
          i18n.changeLanguage(userLang)
        }
      } catch (err) {
        console.error(t('loading_failed'), err)
        setError(t('loading_failed'))
      } finally {
        setLoading(false)
      }
    }

    fetchBuildingData()
  }, [hash, t])

  const handleSectionChange = (sectionId: string) => {
    setActiveSection(sectionId)
  }

  const toggleMobileMap = () => {
    setIsMobileMapOpen(!isMobileMapOpen)
  }

  // Handle map resize
  React.useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) return

      const newWidth = window.innerWidth - e.clientX
      const constrainedWidth = Math.max(320, Math.min(800, newWidth))
      setMapWidth(constrainedWidth)
    }

    const handleMouseUp = () => {
      if (isResizing) {
        setIsResizing(false)
        document.body.style.cursor = ''
        document.body.style.userSelect = ''
      }
    }

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
      document.body.style.cursor = 'col-resize'
      document.body.style.userSelect = 'none'
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [isResizing])

  if (!hash) {
    return (
      <PublicLayout>
        <Container>
          <Stack align="center" justify="center" style={{ minHeight: '60vh' }}>
            <Text variant="h3">{tCommon('validation.invalid_link')}</Text>
          </Stack>
        </Container>
      </PublicLayout>
    )
  }

  return (
    <PublicLayout
      logo={buildingData?.office?.logo || buildingData?.company?.logo}
      companyName={buildingData?.company?.name}
      officeName={buildingData?.office?.name}
      creationDate={buildingData?.creation_date}
      title={
        buildingData?.user
          ? `${buildingData.user.first_name} ${buildingData.user.last_name}`
          : ''
      }
    >
      {loading && (
        <Container>
          <Stack align="center" justify="center" style={{ minHeight: '50vh' }}>
            <Loading size="large" />
            <Text variant="h4">{tCommon('loading')}</Text>
          </Stack>
        </Container>
      )}

      {error && (
        <Container>
          <Stack
            align="center"
            justify="center"
            spacing="lg"
            style={{ minHeight: '50vh' }}
          >
            <Icon name="alert-triangle" size="xl" color="error" />
            <Stack align="center" spacing="sm">
              <Text variant="h3" weight="bold">
                {t('error_loading_data')}
              </Text>
              <Text variant="body1" color="secondary">
                {error}
              </Text>
            </Stack>
            <Button variant="primary" onClick={() => window.location.reload()}>
              {tCommon('try_again')}
            </Button>
          </Stack>
        </Container>
      )}

      {buildingData && !loading && !error && (
        <PublicAnalysisProvider
          entity={buildingData}
          entityType="building"
          userId={buildingData?.hash || hash || ''}
        >
          <AnalysisMapSectionDataProvider>
            <MapSectionDataProvider>
              <FullHeightContainer>
                <PageContainer
                  style={{ top: 0, height: '100%', position: 'relative' }}
                >
                  <PropertyHeader
                    title={
                      buildingData.building?.building_name ||
                      t('building_title', { egid: buildingData.egid || '' })
                    }
                    metadata={{
                      address:
                        buildingData?.address_full ||
                        [
                          buildingData.building?.address,
                          buildingData.building?.postal_code,
                          buildingData.building?.city,
                        ]
                          .filter(Boolean)
                          .join(', ') ||
                        tCommon('status.unknown_location'),
                      details: [
                        buildingData.building?.building_type ||
                          buildingData?.category,
                        buildingData.building?.total_floors ||
                        buildingData?.floor_nb
                          ? tCommon('units.floors', {
                              count:
                                buildingData.building?.total_floors ||
                                buildingData?.floor_nb,
                            })
                          : null,
                        buildingData.building?.total_units ||
                        buildingData?.unit_nb
                          ? tCommon('units.buildingUnits', {
                              count:
                                buildingData.building?.total_units ||
                                buildingData?.unit_nb,
                            })
                          : null,
                      ]
                        .filter(Boolean)
                        .join(' | '),
                      built: buildingData?.building_year
                        ? t('built_year', { year: buildingData?.building_year })
                        : undefined,
                    }}
                    popetyClassification={buildingData.popety_classification}
                  />

                  <MainContainer>
                    <AnalysisSidebar
                      sections={filteredNavigationSections}
                      activeSection={activeSection}
                      onSectionChange={handleSectionChange}
                    />

                    <ContentWrapper>
                      <MainContent>
                        <BuildingAnalysisContent
                          activeSection={activeSection}
                          buildingData={buildingData}
                          hash={hash || ''}
                        />
                      </MainContent>

                      <MapWrapper $isOpen={isMobileMapOpen} $width={mapWidth}>
                        <MapCloseButton
                          onClick={() => setIsMobileMapOpen(false)}
                        >
                          <Icon name="x" size="medium" color="secondary" />
                        </MapCloseButton>
                        <BuildingAnalysisMap
                          buildingId={buildingData.building?.id || ''}
                          coordinates={
                            buildingData.building?.longitude &&
                            buildingData.building?.latitude
                              ? [
                                  buildingData.building.longitude,
                                  buildingData.building.latitude,
                                ]
                              : buildingData.building?.geo_center
                                ? [
                                    buildingData.building.geo_center.lon,
                                    buildingData.building.geo_center.lat,
                                  ]
                                : undefined
                          }
                          geoJson={buildingData?.header?.land?.geo_polygon}
                          buildingGeoJson={buildingData.building?.geo_polygon}
                          buildingHeight={
                            buildingData.building?.height_estimate
                          }
                          buildingDisplayHeight={getBuildingDisplayHeight(
                            buildingData.building?.height_estimate_to_display
                          )}
                          buildingClassification={
                            buildingData.building?.popety_classification ||
                            buildingData.popety_classification
                          }
                          onResizeStart={() => setIsResizing(true)}
                        />
                      </MapWrapper>
                    </ContentWrapper>
                  </MainContainer>

                  <MobileMapToggle onClick={toggleMobileMap}>
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-1.447-.894L15 9m0 8V9m0 0L9 7" />
                    </svg>
                  </MobileMapToggle>
                </PageContainer>
              </FullHeightContainer>
            </MapSectionDataProvider>
          </AnalysisMapSectionDataProvider>
        </PublicAnalysisProvider>
      )}
    </PublicLayout>
  )
}

export default PublicBuildingAnalysisPage
